// src/base/ApplicationError.ts
import { serializeError } from "serialize-error";
import { runtime as runtime2 } from "@repo/env";

// src/utils/errorResponse.ts
import { runtime } from "@repo/env";

// src/utils/isApplicationError.ts
function isApplicationError(error) {
  return error instanceof ApplicationError;
}

// src/utils/errorResponse.ts
function errorResponse(error, { includeDebug = runtime.debugEnabled } = {}) {
  if (isApplicationError(error)) {
    return error.toErrorResponse({ includeDebug });
  }
  let message = "An unexpected error occurred";
  let details = "An unexpected error occurred";
  let stack = "";
  let cause;
  if (error instanceof Error) {
    message = error.message;
    details = error.message;
    stack = error.stack ?? "";
    if (error.cause instanceof Error) {
      cause = errorResponse(error.cause, { includeDebug });
    }
  } else {
    details = String(error);
  }
  const baseResponse = {
    category: "unknown",
    code: "UNKNOWN_ERROR",
    message,
    ...cause !== void 0 && { cause }
  };
  if (includeDebug) {
    return {
      ...baseResponse,
      details,
      debugContext: void 0,
      stack
    };
  }
  return baseResponse;
}

// src/base/ApplicationError.ts
var ApplicationError = class extends Error {
  /**
   * Additional context information for debugging and observability.
   *
   * This data is only exposed in error responses when `includeDebug` is true.
   * Do not rely on this for frontend behaviour - clients will not see this data
   * in production responses.
   *
   * If you need data to be part of the public API contract, add it as a top-level
   * field via `toErrorResponse()` override in your error subclass instead.
   */
  debugContext;
  /**
   * A user-friendly message that can be displayed directly to end-users.
   */
  friendlyMessage;
  /**
   * Whether this error should be reported to error tracking systems.
   */
  reportable;
  constructor(message, options) {
    super(message, { cause: options?.cause });
    this.debugContext = options?.debugContext;
    this.friendlyMessage = options?.friendlyMessage;
    this.reportable = options?.reportable ?? true;
    this.name = this.constructor.name;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
  /**
   * Serialises the error to a plain object for logging or transmission.
   *
   * Delegates to `serialize-error` which handles all own properties (including
   * subclass additions like `retryable`, `retryAfter`), non-enumerable properties
   * like `stack`, cause chains (recursively), and circular references. This ensures
   * that when Pino (or any other serialiser) encounters an `ApplicationError` with
   * a `toJSON`, the output is complete and cause chains are fully expanded.
   */
  toJSON() {
    return serializeError(this);
  }
  /**
   * Converts the error to a response object suitable for API responses.
   *
   * Subclasses can override this method to include additional properties
   * specific to their error type. For example, ValidationError includes
   * an `issues` array, and IntegrationError includes `retryable` and `retryAfter`.
   *
   * @param options - Options controlling the response format
   * @returns A response object with error details, optionally including debug information
   */
  toErrorResponse({
    includeDebug = runtime2.debugEnabled
  } = {}) {
    const cause = this.cause instanceof Error ? errorResponse(this.cause, { includeDebug }) : void 0;
    const base = {
      category: this.category,
      code: this.code,
      message: this.friendlyMessage ?? this.message,
      ...cause !== void 0 && { cause }
    };
    if (includeDebug) {
      return {
        ...base,
        details: this.message,
        debugContext: this.debugContext,
        stack: this.stack ?? "",
        cause: cause || this.cause
      };
    }
    return base;
  }
};

// src/errors/ValidationError.ts
var ValidationError = class extends ApplicationError {
  code = "VALIDATION_ERROR";
  category = "validation";
  /**
   * Array of validation issues describing what went wrong.
   */
  issues;
  constructor(message, options) {
    super(message, { ...options, reportable: options?.reportable ?? false });
    this.issues = options?.issues ?? [];
  }
  /**
   * Converts the validation error to a response object.
   *
   * The `issues` array is always included in the response. The debug-only
   * fields on each issue (`value`, `details`, `debugContext`) are included
   * only when `includeDebug` is true, to avoid leaking invalid input values
   * or internal diagnostic data in production responses.
   *
   * @param options - Options controlling the response format
   * @returns A response object with validation issues
   */
  toErrorResponse({
    includeDebug = false
  } = {}) {
    const base = super.toErrorResponse({ includeDebug });
    if (includeDebug) {
      return {
        ...base,
        issues: this.issues
      };
    }
    const publicIssues = this.issues.map(({ field, message, code }) => ({
      field,
      message,
      code
    }));
    return {
      ...base,
      issues: publicIssues
    };
  }
};

// src/errors/NotFoundError.ts
var NotFoundError = class extends ApplicationError {
  code = "NOT_FOUND";
  category = "not_found";
  constructor(resourceType, identifier, options) {
    const identifierStr = typeof identifier === "string" ? identifier : JSON.stringify(identifier);
    super(`${resourceType} not found: ${identifierStr}`, {
      ...options,
      reportable: options?.reportable ?? false,
      debugContext: {
        ...options?.debugContext,
        resourceType,
        identifier
      }
    });
  }
};

// src/errors/ForbiddenError.ts
var ForbiddenError = class extends ApplicationError {
  code = "FORBIDDEN";
  category = "forbidden";
  constructor(message, options) {
    super(message, {
      ...options,
      reportable: options?.reportable ?? false,
      debugContext: {
        ...options?.debugContext,
        resource: options?.resource,
        action: options?.action
      }
    });
  }
};

// src/errors/UnauthorisedError.ts
var UnauthorisedError = class extends ApplicationError {
  code = "UNAUTHORISED";
  category = "unauthorised";
  constructor(message = "Authentication required", options) {
    super(message, { ...options, reportable: options?.reportable ?? false });
  }
};

// src/errors/BadRequestError.ts
var BadRequestError = class extends ApplicationError {
  code = "BAD_REQUEST";
  category = "validation";
  constructor(message, options) {
    super(message, { ...options, reportable: options?.reportable ?? false });
  }
};

// src/errors/ConflictError.ts
var ConflictError = class extends ApplicationError {
  code = "CONFLICT";
  category = "conflict";
  constructor(message, options) {
    super(message, {
      ...options,
      reportable: options?.reportable ?? false,
      debugContext: {
        ...options?.debugContext,
        conflictingResource: options?.conflictingResource
      }
    });
  }
};

// src/errors/BusinessRuleError.ts
var BusinessRuleError = class extends ApplicationError {
  code = "BUSINESS_RULE_VIOLATION";
  category = "business_rule";
  constructor(message, options) {
    super(message, {
      ...options,
      reportable: options?.reportable ?? false,
      debugContext: {
        ...options.debugContext,
        rule: options.rule
      }
    });
  }
};

// src/errors/IntegrationError.ts
var IntegrationError = class extends ApplicationError {
  code = "INTEGRATION_ERROR";
  category = "integration";
  /**
   * Whether the operation can be retried.
   */
  retryable;
  /**
   * Number of seconds to wait before retrying (if retryable).
   */
  retryAfter;
  constructor(message, options) {
    super(message, {
      ...options,
      debugContext: {
        ...options?.debugContext,
        service: options.service,
        statusCode: options?.statusCode
      }
    });
    this.retryable = options?.retryable ?? true;
    this.retryAfter = options?.retryAfter;
  }
  /**
   * Converts the integration error to a response object.
   *
   * The `retryable` and `retryAfter` fields are always included in the response
   * to allow clients to implement retry logic.
   *
   * @param options - Options controlling the response format
   * @returns A response object with retry information
   */
  toErrorResponse({
    includeDebug = false
  } = {}) {
    const base = super.toErrorResponse({ includeDebug });
    return {
      ...base,
      retryable: this.retryable,
      ...this.retryAfter !== void 0 && { retryAfter: this.retryAfter }
    };
  }
};

// src/errors/SystemError.ts
var SystemError = class extends ApplicationError {
  code = "SYSTEM_ERROR";
  category = "system";
  constructor(message, options) {
    super(message, options);
  }
};

// src/errors/NotImplementedError.ts
var NotImplementedError = class extends ApplicationError {
  code = "NOT_IMPLEMENTED";
  category = "not_implemented";
  constructor(feature, options) {
    super(`Functionality not yet implemented: ${feature}`, {
      ...options,
      debugContext: { ...options?.debugContext, feature }
    });
  }
};

// src/errors/ZodValidationError.ts
var ZodValidationError = class extends ValidationError {
  code = "ZOD_VALIDATION_ERROR";
  constructor(zodError, options) {
    const issues = zodError.issues.map((issue) => ({
      field: issue.path.join("."),
      message: issue.message,
      code: issue.code,
      value: void 0
      // Zod doesn't always provide the value
    }));
    super("Validation failed", {
      ...options,
      issues,
      debugContext: {
        ...options?.debugContext,
        zodIssues: zodError.issues
      }
    });
  }
};

// src/utils/errorToHttpStatus.ts
function errorToHttpStatus(error) {
  switch (error.category) {
    case "validation":
      return 400;
    case "unauthorised":
      return 401;
    case "forbidden":
      return 403;
    case "not_found":
      return 404;
    case "conflict":
      return 409;
    case "business_rule":
      return 422;
    case "integration":
      return 502;
    case "not_implemented":
      return 501;
    case "system":
    default:
      return 500;
  }
}

// src/utils/errorCategoryToGraphQLCode.ts
function errorCategoryToGraphQLCode(category) {
  switch (category) {
    case "validation":
      return "BAD_USER_INPUT";
    case "unauthorised":
      return "UNAUTHENTICATED";
    case "forbidden":
      return "FORBIDDEN";
    case "not_found":
      return "NOT_FOUND";
    case "conflict":
      return "CONFLICT";
    case "business_rule":
      return "UNPROCESSABLE_CONTENT";
    case "integration":
      return "UPSTREAM_ERROR";
    case "not_implemented":
      return "NOT_IMPLEMENTED";
    case "system":
    default:
      return "INTERNAL_SERVER_ERROR";
  }
}

// src/middleware/express.middleware.ts
import { runtime as runtime3 } from "@repo/env";
function expressErrorMiddleware({
  logger = console,
  onError,
  transformResponse,
  debug = runtime3.debugEnabled
} = {}) {
  return async (error, req, res, next) => {
    if (res.headersSent) {
      return next(error);
    }
    const statusCode = getStatusCode(error);
    const resolvedLogger = typeof logger === "function" ? logger(req) : logger;
    const includeDebug = typeof debug === "function" ? debug(req) : debug;
    const formattedError = errorResponse(error, { includeDebug });
    logError(resolvedLogger, error, req, statusCode, formattedError);
    if (onError) {
      try {
        await onError({ error, req, statusCode });
      } catch (callbackError) {
        resolvedLogger.error(
          {
            path: req.path,
            method: req.method,
            error: callbackError
          },
          "Error in onError callback"
        );
      }
    }
    const responseBody = transformResponse ? transformResponse({ error: formattedError }) : { error: formattedError };
    res.status(statusCode).json(responseBody);
  };
}
function getStatusCode(error) {
  if (isApplicationError(error)) {
    return errorToHttpStatus(error);
  }
  return 500;
}
function logError(logger, error, req, statusCode, formattedError) {
  if (isApplicationError(error) && !error.reportable) {
    return;
  }
  const logMeta = {
    statusCode,
    category: formattedError.category,
    code: formattedError.code,
    path: req.path,
    method: req.method,
    error: error instanceof Error ? error : { message: String(error) }
  };
  const msg = error instanceof Error ? error.message : formattedError.message;
  logger.error(logMeta, msg);
}

// ../../node_modules/graphql/jsutils/isObjectLike.mjs
function isObjectLike(value) {
  return typeof value == "object" && value !== null;
}

// ../../node_modules/graphql/jsutils/invariant.mjs
function invariant(condition, message) {
  const booleanCondition = Boolean(condition);
  if (!booleanCondition) {
    throw new Error(
      message != null ? message : "Unexpected invariant triggered."
    );
  }
}

// ../../node_modules/graphql/language/location.mjs
var LineRegExp = /\r\n|[\n\r]/g;
function getLocation(source, position) {
  let lastLineStart = 0;
  let line = 1;
  for (const match of source.body.matchAll(LineRegExp)) {
    typeof match.index === "number" || invariant(false);
    if (match.index >= position) {
      break;
    }
    lastLineStart = match.index + match[0].length;
    line += 1;
  }
  return {
    line,
    column: position + 1 - lastLineStart
  };
}

// ../../node_modules/graphql/language/printLocation.mjs
function printLocation(location) {
  return printSourceLocation(
    location.source,
    getLocation(location.source, location.start)
  );
}
function printSourceLocation(source, sourceLocation) {
  const firstLineColumnOffset = source.locationOffset.column - 1;
  const body = "".padStart(firstLineColumnOffset) + source.body;
  const lineIndex = sourceLocation.line - 1;
  const lineOffset = source.locationOffset.line - 1;
  const lineNum = sourceLocation.line + lineOffset;
  const columnOffset = sourceLocation.line === 1 ? firstLineColumnOffset : 0;
  const columnNum = sourceLocation.column + columnOffset;
  const locationStr = `${source.name}:${lineNum}:${columnNum}
`;
  const lines = body.split(/\r\n|[\n\r]/g);
  const locationLine = lines[lineIndex];
  if (locationLine.length > 120) {
    const subLineIndex = Math.floor(columnNum / 80);
    const subLineColumnNum = columnNum % 80;
    const subLines = [];
    for (let i = 0; i < locationLine.length; i += 80) {
      subLines.push(locationLine.slice(i, i + 80));
    }
    return locationStr + printPrefixedLines([
      [`${lineNum} |`, subLines[0]],
      ...subLines.slice(1, subLineIndex + 1).map((subLine) => ["|", subLine]),
      ["|", "^".padStart(subLineColumnNum)],
      ["|", subLines[subLineIndex + 1]]
    ]);
  }
  return locationStr + printPrefixedLines([
    // Lines specified like this: ["prefix", "string"],
    [`${lineNum - 1} |`, lines[lineIndex - 1]],
    [`${lineNum} |`, locationLine],
    ["|", "^".padStart(columnNum)],
    [`${lineNum + 1} |`, lines[lineIndex + 1]]
  ]);
}
function printPrefixedLines(lines) {
  const existingLines = lines.filter(([_, line]) => line !== void 0);
  const padLen = Math.max(...existingLines.map(([prefix]) => prefix.length));
  return existingLines.map(([prefix, line]) => prefix.padStart(padLen) + (line ? " " + line : "")).join("\n");
}

// ../../node_modules/graphql/error/GraphQLError.mjs
function toNormalizedOptions(args) {
  const firstArg = args[0];
  if (firstArg == null || "kind" in firstArg || "length" in firstArg) {
    return {
      nodes: firstArg,
      source: args[1],
      positions: args[2],
      path: args[3],
      originalError: args[4],
      extensions: args[5]
    };
  }
  return firstArg;
}
var GraphQLError = class _GraphQLError extends Error {
  /**
   * An array of `{ line, column }` locations within the source GraphQL document
   * which correspond to this error.
   *
   * Errors during validation often contain multiple locations, for example to
   * point out two things with the same name. Errors during execution include a
   * single location, the field which produced the error.
   *
   * Enumerable, and appears in the result of JSON.stringify().
   */
  /**
   * An array describing the JSON-path into the execution response which
   * corresponds to this error. Only included for errors during execution.
   *
   * Enumerable, and appears in the result of JSON.stringify().
   */
  /**
   * An array of GraphQL AST Nodes corresponding to this error.
   */
  /**
   * The source GraphQL document for the first location of this error.
   *
   * Note that if this Error represents more than one node, the source may not
   * represent nodes after the first node.
   */
  /**
   * An array of character offsets within the source GraphQL document
   * which correspond to this error.
   */
  /**
   * The original error thrown from a field resolver during execution.
   */
  /**
   * Extension fields to add to the formatted error.
   */
  /**
   * @deprecated Please use the `GraphQLErrorOptions` constructor overload instead.
   */
  constructor(message, ...rawArgs) {
    var _this$nodes, _nodeLocations$, _ref;
    const { nodes, source, positions, path, originalError, extensions } = toNormalizedOptions(rawArgs);
    super(message);
    this.name = "GraphQLError";
    this.path = path !== null && path !== void 0 ? path : void 0;
    this.originalError = originalError !== null && originalError !== void 0 ? originalError : void 0;
    this.nodes = undefinedIfEmpty(
      Array.isArray(nodes) ? nodes : nodes ? [nodes] : void 0
    );
    const nodeLocations = undefinedIfEmpty(
      (_this$nodes = this.nodes) === null || _this$nodes === void 0 ? void 0 : _this$nodes.map((node) => node.loc).filter((loc) => loc != null)
    );
    this.source = source !== null && source !== void 0 ? source : nodeLocations === null || nodeLocations === void 0 ? void 0 : (_nodeLocations$ = nodeLocations[0]) === null || _nodeLocations$ === void 0 ? void 0 : _nodeLocations$.source;
    this.positions = positions !== null && positions !== void 0 ? positions : nodeLocations === null || nodeLocations === void 0 ? void 0 : nodeLocations.map((loc) => loc.start);
    this.locations = positions && source ? positions.map((pos) => getLocation(source, pos)) : nodeLocations === null || nodeLocations === void 0 ? void 0 : nodeLocations.map((loc) => getLocation(loc.source, loc.start));
    const originalExtensions = isObjectLike(
      originalError === null || originalError === void 0 ? void 0 : originalError.extensions
    ) ? originalError === null || originalError === void 0 ? void 0 : originalError.extensions : void 0;
    this.extensions = (_ref = extensions !== null && extensions !== void 0 ? extensions : originalExtensions) !== null && _ref !== void 0 ? _ref : /* @__PURE__ */ Object.create(null);
    Object.defineProperties(this, {
      message: {
        writable: true,
        enumerable: true
      },
      name: {
        enumerable: false
      },
      nodes: {
        enumerable: false
      },
      source: {
        enumerable: false
      },
      positions: {
        enumerable: false
      },
      originalError: {
        enumerable: false
      }
    });
    if (originalError !== null && originalError !== void 0 && originalError.stack) {
      Object.defineProperty(this, "stack", {
        value: originalError.stack,
        writable: true,
        configurable: true
      });
    } else if (Error.captureStackTrace) {
      Error.captureStackTrace(this, _GraphQLError);
    } else {
      Object.defineProperty(this, "stack", {
        value: Error().stack,
        writable: true,
        configurable: true
      });
    }
  }
  get [Symbol.toStringTag]() {
    return "GraphQLError";
  }
  toString() {
    let output = this.message;
    if (this.nodes) {
      for (const node of this.nodes) {
        if (node.loc) {
          output += "\n\n" + printLocation(node.loc);
        }
      }
    } else if (this.source && this.locations) {
      for (const location of this.locations) {
        output += "\n\n" + printSourceLocation(this.source, location);
      }
    }
    return output;
  }
  toJSON() {
    const formattedError = {
      message: this.message
    };
    if (this.locations != null) {
      formattedError.locations = this.locations;
    }
    if (this.path != null) {
      formattedError.path = this.path;
    }
    if (this.extensions != null && Object.keys(this.extensions).length > 0) {
      formattedError.extensions = this.extensions;
    }
    return formattedError;
  }
};
function undefinedIfEmpty(array) {
  return array === void 0 || array.length === 0 ? void 0 : array;
}

// src/middleware/graphql.ts
import { runtime as runtime4 } from "@repo/env";
function createGraphQLErrorFormatter({
  includeDebug = runtime4.debugEnabled
} = {}) {
  return (error) => {
    const formattedError = errorResponse(error, { includeDebug });
    const { message, ...extensions } = formattedError;
    extensions.graphqlCode = errorCategoryToGraphQLCode(
      formattedError.category
    );
    return new GraphQLError(message, { extensions });
  };
}
var formatGraphQLError = createGraphQLErrorFormatter();
export {
  ApplicationError,
  BadRequestError,
  BusinessRuleError,
  ConflictError,
  ForbiddenError,
  IntegrationError,
  NotFoundError,
  NotImplementedError,
  SystemError,
  UnauthorisedError,
  ValidationError,
  ZodValidationError,
  createGraphQLErrorFormatter,
  errorCategoryToGraphQLCode,
  errorResponse,
  errorToHttpStatus,
  expressErrorMiddleware,
  formatGraphQLError,
  isApplicationError
};
//# sourceMappingURL=index.js.map